/**
 * **把一段文本存成文件。**
 *
 * ## ⚠️ 这是 UI 层里**唯一**碰 DOM 的地方，理由要写清楚
 *
 * 项目规矩是「UI 尽可能放在 Pixi 内部」，`architecture.md` §7.6 甚至定死了
 * "一个 DOM 输入框都不放"。但**下载文件不是画界面**：
 *
 * - 它没有版面、没有尺寸、没有悬停状态 —— 没有"画"这件事
 * - 浏览器**没有**给 Pixi（或任何图形库）提供"保存文件"的接口，
 *   `Blob` + `URL.createObjectURL` + 一个临时 `<a>` 是唯一的平台做法
 *
 * 所以这里用的是平台 API，不是用 DOM 做布局。区别是实质性的：
 * 换成 Pixi 渲染不会有任何改善，只会有更多代码。
 *
 * ⚠️ 临时的 `<a>` 用完必须 `remove()` 且 `revokeObjectURL()` ——
 * 不撤销的话那个 blob 会一直留在内存里，导出几次就泄漏几 MB。
 */

import { t } from '../i18n';

export interface DownloadResult {
  readonly ok: boolean;
  readonly reason?: string;
}

/**
 * 触发一次下载。
 *
 * `URL.createObjectURL` 在极少数环境（某些嵌入式 WebView、无 Blob 支持）
 * 不存在，所以这里不抛异常 —— 导不出文件是小事，把设置页搞崩是大事。
 */
export function downloadText(filename: string, text: string, mime = 'application/json'): DownloadResult {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return { ok: false, reason: t('transfer.unsupported') };
  }
  let url: string | null = null;
  try {
    const blob = new Blob([text], { type: `${mime};charset=utf-8` });
    url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : t('transfer.failed') };
  } finally {
    // ⚠️ 撤销要等一拍：立刻撤销在部分浏览器上会把刚开始的下载掐断
    if (url !== null) {
      const pending = url;
      setTimeout(() => URL.revokeObjectURL(pending), 10_000);
    }
  }
}

/**
 * 导出的文件名：`ivory-roulette-<标签>-<时间戳>.json`。
 *
 * 带时间戳是因为这东西会被反复导出（调参、换号），
 * 不带的话下载目录里会堆一串 `settings (3).json`。
 */
export function exportFilename(tag: string, now: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `ivory-roulette-${tag}-${stamp}.json`;
}
